import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { X, Pen, Plus } from 'lucide-react';
import { z } from 'zod';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { QaCriterion, QaCriteriaType } from '../types';

interface QaCriteriaDialogProps {
  criterion?: QaCriterion;
  currentTotalWeight: number;
  type: QaCriteriaType;
  onSave: (data: { id?: number; name: string; description: string; weight: number; type: QaCriteriaType }) => void;
}

export function QaCriteriaDialog({
  criterion,
  currentTotalWeight,
  type,
  onSave,
}: QaCriteriaDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isEdit = !!criterion;

  // Max weight this criterion can have: 100 - otherWeights
  const otherWeights = isEdit
    ? currentTotalWeight - (criterion?.weight ?? 0)
    : currentTotalWeight;
  const maxWeight = 100 - otherWeights;

  const schema = z.object({
    name: z.string().min(1, t('nameIsRequired') || 'Name is required'),
    description: z.string().min(1, t('qa.descriptionRequired') || 'Description is required'),
    weight: z
      .number({ invalid_type_error: t('qa.weightRequired') || 'Weight is required' })
      .min(1, t('qa.weightMin') || 'Weight must be at least 1%')
      .max(maxWeight, t('qa.weightExceedsLimit', { max: maxWeight }) || `Max weight is ${maxWeight}%`),
  });

  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      weight: undefined as unknown as number,
    },
  });

  useEffect(() => {
    if (open && criterion) {
      form.reset({
        name: criterion.name,
        description: criterion.description,
        weight: criterion.weight,
      });
    } else if (open && !criterion) {
      form.reset({
        name: '',
        description: '',
        weight: undefined as unknown as number,
      });
    }
  }, [open, criterion, form]);

  const handleSubmit = (values: FormValues) => {
    onSave({
      id: criterion?.id,
      name: values.name,
      description: values.description,
      weight: values.weight,
      type,
    });
    setOpen(false);
    form.reset();
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {isEdit ? (
          <Button size="sm" variant="outline" className="rounded-r-none">
            <Pen className="h-4 w-4" />
          </Button>
        ) : (
          <Button size="sm" disabled={currentTotalWeight === 100}>
            <Plus className="h-4 w-4 mr-1" />
            {t('qa.addCriterion')}
          </Button>
        )}
      </AlertDialogTrigger>

      <AlertDialogContent className="p-6 max-w-lg">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9]">
          <AlertDialogTitle className="text-xl font-semibold">
            {isEdit ? t('qa.editCriterion') : t('qa.addCriterion')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => setOpen(false)} aria-label={t('closeDialog')}>
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="mt-4 flex flex-col gap-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t('name')} <sup className="text-red-500">*</sup>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t('qa.criterionNamePlaceholder')} {...field} />
                  </FormControl>
                  {form.formState.errors.name && (
                    <FormMessage>{form.formState.errors.name.message}</FormMessage>
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t('description')} <sup className="text-red-500">*</sup>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('qa.criterionDescriptionPlaceholder')}
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  {form.formState.errors.description && (
                    <FormMessage>{form.formState.errors.description.message}</FormMessage>
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="weight"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t('qa.weight')} (%) <sup className="text-red-500">*</sup>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={maxWeight}
                      placeholder={t('qa.weightPlaceholder', { max: maxWeight })}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val ? parseInt(val, 10) : undefined);
                      }}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    {t('qa.availableWeight')}: {maxWeight}%
                  </p>
                  {form.formState.errors.weight && (
                    <FormMessage>{form.formState.errors.weight.message}</FormMessage>
                  )}
                </FormItem>
              )}
            />

            <AlertDialogFooter className="flex justify-end gap-2 mt-4">
              <AlertDialogCancel onClick={() => setOpen(false)}>
                {t('cancel')}
              </AlertDialogCancel>
              <Button type="submit">
                {t('save')}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
